/**
 * Facebook Sign-In button — uses Meta JS SDK popup flow.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

const FB_VERSION = "v25.0";
let sdkPromise: Promise<void> | null = null;

function loadFacebookSdk(appId: string): Promise<void> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    if (window.FB) { resolve(); return; }
    window.fbAsyncInit = () => {
      window.FB.init({ appId, cookie: false, xfbml: false, version: FB_VERSION });
      resolve();
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.async = true; s.defer = true; s.crossOrigin = "anonymous";
    s.onerror = () => { sdkPromise = null; reject(new Error("Could not load Facebook SDK")); };
    document.body.appendChild(s);
  });
  return sdkPromise;
}

export const FacebookSignInButton: React.FC<{ mode?: "signin" | "signup"; onDone?: () => void }> = ({ mode = "signin", onDone }) => {
  const nav = useNavigate();
  const auth = useAuth();
  const [appId, setAppId] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/facebook/config")
      .then((r) => { if (r.data.configured) setAppId(r.data.app_id); })
      .catch(() => {});
  }, []);

  if (!appId) return null;

  const click = async () => {
    setLoading(true);
    try {
      await loadFacebookSdk(appId);
      window.FB.login(async (response: any) => {
        try {
          const at = response?.authResponse?.accessToken;
          if (!at) { setLoading(false); return; }
          const { data } = await api.post("/auth/facebook", { access_token: at });
          localStorage.setItem("socibaba_token", data.token);
          if ((auth as any).loginWithToken) {
            await (auth as any).loginWithToken(data.token, data.user);
          }
          toast.success(`Welcome, ${data.user?.name || "friend"}!`);
          onDone?.();
          nav("/dashboard");
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || "Facebook sign-in failed");
        } finally {
          setLoading(false);
        }
      }, { scope: "public_profile,email", return_scopes: true });
    } catch (e: any) {
      setLoading(false);
      toast.error(e?.message || "Could not open Facebook popup");
    }
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={loading}
      data-testid={`facebook-signin-${mode}`}
      className="w-full h-10 flex items-center justify-center gap-2 rounded-full border border-[color:var(--sg-border)] bg-white dark:bg-[color:var(--sg-card)] text-[#0F172A] dark:text-[color:var(--sg-fg)] font-medium text-sm hover:border-[#1877F2] transition-colors disabled:opacity-60"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
        <svg className="h-4 w-4" viewBox="0 0 32 32" fill="#1877F2" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M32 16A16 16 0 1 0 13.5 31.8V20.6H9.4V16h4.1v-3.6c0-4 2.4-6.2 6-6.2 1.7 0 3.5.3 3.5.3v3.9h-2c-2 0-2.6 1.2-2.6 2.5V16h4.4l-.7 4.6h-3.7v11.2A16 16 0 0 0 32 16z"/>
        </svg>
      )}
      <span>{mode === "signup" ? "Sign up with Facebook" : "Continue with Facebook"}</span>
    </button>
  );
};

export default FacebookSignInButton;
